import React, { useState, useRef, useEffect } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCanCalculateBalance } from '../hooks/useCanCalculateBalance.ts';

const SmsTemplateEditor = ({
  templateType,
  template,
  availablePlaceholders,
  placeholderDescriptions = {},
  onSave,
  onDelete,
  isLoading = false,
  label = 'Template',
  rows = 4,
  maxLength = 500,
  canCalculateBalanceOverride = null // If provided, use this instead of hook
}) => {
  const [value, setValue] = useState(template || '');
  const [invalidPlaceholders, setInvalidPlaceholders] = useState([]);
  const [hasValidated, setHasValidated] = useState(false);
  const textareaRef = useRef(null);
  const { canCalculateBalance: canCalculateBalanceFromHook } = useCanCalculateBalance();
  
  // Use override if provided (from local unsaved settings), otherwise use hook (saved settings)
  const canCalculateBalance = canCalculateBalanceOverride !== null ? canCalculateBalanceOverride : canCalculateBalanceFromHook;
  
  // Balance-related placeholders that require balance calculation
  const balancePlaceholders = ['balance', 'payment_status', 'overpaid_amount', 'contribution_balance'];
  
  // Don't filter - show all placeholders but disable balance ones if can't calculate

  // Update value when template prop changes
  useEffect(() => {
    if (template !== undefined) {
      setValue(template);
      setHasValidated(false);
      setInvalidPlaceholders([]);
    }
  }, [template]);

  // Validate placeholders
  const validatePlaceholders = (msg) => {
    if (!msg) {
      setInvalidPlaceholders([]);
      return true;
    }
    const placeholderPattern = /\{([^}]+)\}/g;
    const foundPlaceholders = [];
    let match;
    while ((match = placeholderPattern.exec(msg)) !== null) {
      foundPlaceholders.push(match[1]);
    }
    
    // Check for invalid placeholders (not in available list OR balance placeholders when balance can't be calculated)
    const invalid = foundPlaceholders.filter(ph => {
      // If it's a balance placeholder and balance can't be calculated, it's invalid
      if (balancePlaceholders.includes(ph) && !canCalculateBalance) {
        return true;
      }
      // If it's not in available placeholders, it's invalid
      if (!availablePlaceholders.includes(ph)) {
        return true;
      }
      return false;
    });
    
    setInvalidPlaceholders(invalid);
    return invalid.length === 0;
  };

  // Format message preview with highlighted placeholders
  const formatMessagePreview = (msg) => {
    if (!msg) return '';
    const parts = msg.split(/(\{[^}]+\})/g);
    return parts.map((part, index) => {
      if (part.match(/^\{[^}]+\}$/)) {
        const placeholderName = part.slice(1, -1);
        const isValid = availablePlaceholders.includes(placeholderName);
        return (
          <span
            key={index}
            className={isValid ? 'px-1 py-0.5 rounded bg-green-100 text-green-800 font-mono text-sm' : 'px-1 py-0.5 rounded bg-red-100 text-red-800 font-mono text-sm'}
          >
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Handle change
  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (hasValidated) {
      validatePlaceholders(newValue);
    }
  };

  // Handle blur - validate when user leaves the field
  const handleBlur = () => {
    setHasValidated(true);
    const isValid = validatePlaceholders(value);
    if (!isValid && invalidPlaceholders.length > 0) {
      toast.error(
        `Invalid placeholders: {${invalidPlaceholders.join('}, {')}}. ` +
        `Available: {${availablePlaceholders.join('}, {')}}`
      );
    }
  };

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const placeholderText = `{${placeholder}}`;
    const newValue = value.substring(0, start) + placeholderText + value.substring(end);
    
    setValue(newValue);
    setHasValidated(false);
    setInvalidPlaceholders([]);

    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholderText.length, start + placeholderText.length);
    }, 0);
  };

  const handleSave = () => {
    setHasValidated(true);
    if (!validatePlaceholders(value)) {
      toast.error('Please fix invalid placeholders before saving.');
      return;
    }
    if (onSave) {
      onSave(value);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this template? The system default will be used instead.')) {
      if (onDelete) {
        onDelete();
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {label}
        </label>

        {/* Placeholder Buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
          <span className="text-xs font-medium text-gray-600">Quick insert:</span>
          {availablePlaceholders.map((ph) => {
            const isBalancePlaceholder = balancePlaceholders.includes(ph);
            const isDisabled = isBalancePlaceholder && !canCalculateBalance;
            const tooltipText = isDisabled 
              ? 'Balance placeholders require member targets or active drives to be set in Payment Routing settings'
              : (placeholderDescriptions[ph] || `Insert {${ph}}`);
            
            return (
              <button
                key={ph}
                type="button"
                onClick={() => !isDisabled && insertPlaceholder(ph)}
                disabled={isDisabled}
                className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border transition-all shadow-sm ${
                  isDisabled
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                    : 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:border-green-400 active:bg-green-200 cursor-pointer'
                }`}
                title={tooltipText}
              >
                <Plus className="w-3 h-3 mr-1" />
                <span className="font-mono">{'{'}{ph}{'}'}</span>
              </button>
            );
          })}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          rows={rows}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={maxLength}
          className={`px-3 py-2 w-full text-sm rounded-md border focus:ring-blue-500 focus:border-blue-500 ${
            invalidPlaceholders.length > 0 && hasValidated
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300'
          }`}
          placeholder="Enter your SMS template with placeholders..."
        />
        <div className="text-xs text-gray-500 text-right mt-1">
          {value.length}/{maxLength} characters
        </div>

        {/* Preview with formatted placeholders */}
        {value && (
          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
            <p className="text-xs text-gray-500 mb-1 font-medium">Preview:</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formatMessagePreview(value)}
            </p>
          </div>
        )}

        {/* Error message */}
        {invalidPlaceholders.length > 0 && hasValidated && (
          <div className="mt-2 p-2 bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-red-700 font-medium mb-1">
                  Invalid placeholders found:
                </p>
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {invalidPlaceholders.map((ph) => (
                    <li key={ph}>{'{'}{ph}{'}'} is not valid</li>
                  ))}
                </ul>
                <p className="text-xs text-red-600 mt-1">
                  Valid placeholders: {availablePlaceholders.map(ph => `{${ph}}`).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}


        {/* Help text */}
        <p className="text-xs text-gray-500 mt-2">
          Available placeholders: {availablePlaceholders.map(ph => `{${ph}}`).join(', ')}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-2">
        {onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete Template
          </button>
        )}
        {onSave && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Template'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SmsTemplateEditor;


